import * as AuthActions from '../actions/auth.actions';
import * as principalsActions from '../actions/principals.actions';
import * as userObjectActions from '../actions/core.actions';

import { Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Dashboard, Workspace } from '../models/core.models';
import { from, of } from 'rxjs';
import { ObjectService, Principal } from '../services/object.service';
import { UserService } from '../services/user.service';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  createFeedSuccess,
  duplicateFeedsSuccess,
  editFeedSuccess,
  loadFeedSuccess,
} from '../actions/feed.actions';
import {
  duplicateDashboardsSuccess,
  loadDashboardSuccess,
} from '../actions/dashboard.actions';
import { setHomeObjectsLifeCycle } from '../actions/home.actions';
import {
  selectCoreAppSection,
  selectCoreAvailableModules,
  selectCoreDarkMode,
  selectMainMenuExpanded,
  selectPrincipals,
  selectUser,
  selectUserAccessLevel,
} from '../reducers';

import { AppState } from 'src/app/reducers';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { userWorkspacesQueriesFilter } from 'src/app/api/queries/workspace.queries';
import { augmentObjectWithIconAndImage } from 'src/app/shared/utils/object-utils';
import { UserApiService } from 'src/app/api/queries/user-api.service';
import { UserWorkspacesService } from '../services/user-workspaces.service';
import { WorkspaceObjectsService } from '../services/workspace-objects.service';
import { RecentObjectsService } from '../services/recent-objects.service';
import { Injectable, inject } from '@angular/core';
import { NgToastService } from 'ng-angular-popup';
import { Router } from '@angular/router';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { WidgetViewPresetService } from '../services/widget-view-preset.service';
import { concatLatestFrom } from '@ngrx/operators';
import { toggleMainMenuExpanded } from '../actions/main-menu.actions';
import { language } from '@codemirror/language';

// The four operations whose response rows leave the list the user is looking at.
const MASS_OPERATIONS_REMOVING_ROWS = [
  'unarchive',
  'undelete',
  'delete',
  'archive',
];

@Injectable()
export class CoreEffects {
  /**
   * Server-confirmed writes into the workspace list. Call sites keep broadcasting
   * the `…Success` actions - `selected.reducer` needs them for the selection reset.
   *
   * Patch, not invalidate: the response carries the row, so no refetch is needed.
   */
  patchWorkspaceListCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          userObjectActions.createWorkspaceSuccess,
          userObjectActions.updateWorkspaceSuccess,
          userObjectActions.massObjectOperationSuccess,
        ),
        tap((action) => {
          this.queryClient.setQueriesData<Workspace[]>(
            userWorkspacesQueriesFilter(),
            (workspaces) => {
              if (!workspaces) {
                return workspaces;
              }

              if (
                action.type === userObjectActions.createWorkspaceSuccess.type
              ) {
                return [
                  ...workspaces,
                  augmentObjectWithIconAndImage(action.workspace) as Workspace,
                ];
              }

              if (
                action.type === userObjectActions.updateWorkspaceSuccess.type
              ) {
                const updated = action.workspace;
                return workspaces.map((workspace) =>
                  workspace.id === updated.id
                    ? (augmentObjectWithIconAndImage(updated) as Workspace)
                    : workspace,
                );
              }

              const { response } = action;
              if (!MASS_OPERATIONS_REMOVING_ROWS.includes(response.operation)) {
                return workspaces;
              }

              const removedIds = (response.data.workspaces ?? []).map(
                (w) => w.id,
              );
              return workspaces.filter((w) => !removedIds.includes(w.id));
            },
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * The same arrangement for a workspace's object lists. `massObjectOperationSuccess`
   * and `moveObjectsSuccess` additionally feed `selected.reducer`'s selection reset,
   * so they have to survive regardless.
   */
  patchWorkspaceObjectsCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          loadFeedSuccess,
          loadDashboardSuccess,
          editFeedSuccess,
          createFeedSuccess,
          duplicateFeedsSuccess,
          userObjectActions.createDashboardSuccess,
          userObjectActions.updateDashboardSuccess,
          duplicateDashboardsSuccess,
          userObjectActions.moveObjectsSuccess,
          userObjectActions.massObjectOperationSuccess,
        ),
        tap((action) => {
          switch (action.type) {
            case loadFeedSuccess.type:
              this.workspaceObjects.upsertFeed(action.feed);
              break;

            case loadDashboardSuccess.type:
              this.workspaceObjects.upsertDashboard(action.dashboard);
              break;

            case editFeedSuccess.type:
              this.workspaceObjects.replaceFeed(action.feedId, action.feed);
              break;

            case createFeedSuccess.type:
              this.workspaceObjects.appendFeed(action.feed);
              break;

            case duplicateFeedsSuccess.type:
              this.workspaceObjects.appendFeeds(action.response.feeds);
              break;

            case userObjectActions.createDashboardSuccess.type:
              this.workspaceObjects.appendDashboard(action.dashboard);
              break;

            case userObjectActions.updateDashboardSuccess.type:
              this.workspaceObjects.replaceDashboard(action.dashboard);
              break;

            case duplicateDashboardsSuccess.type:
              this.workspaceObjects.appendDashboards(
                action.response.dashboards,
              );
              break;

            case userObjectActions.moveObjectsSuccess.type:
              this.workspaceObjects.mergeMovedObjects(action.objects);
              break;

            case userObjectActions.massObjectOperationSuccess.type:
              if (
                MASS_OPERATIONS_REMOVING_ROWS.includes(
                  action.response.operation,
                )
              ) {
                this.workspaceObjects.removeObjects(action.response);
              }
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  listCompanyObjectsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.listCompanyObjectsSuccess),
        // or withLatestFrom(this.store.select(selectUser)) – both work
        concatLatestFrom(() => this.store.select(selectUser)),
        tap(([, user]) => {
          // If there is no user yet, do nothing.
          if (!user) {
            return;
          }
          // The toggle changes which objects the user may see, so both lists have
          // to be refetched.
          this.userWorkspaces.invalidate();
          this.recentObjects.invalidate();
        }),
      ),
    { dispatch: false },
  );

  /**
   * `authInitFactory` dispatches `loginSuccess` from an `APP_INITIALIZER`, before any
   * observer exists, so do not add query invalidations here - they would have nothing
   * to match. The lists are fetched by whoever mounts first instead.
   */
  persistDarkModeOnLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess, AuthActions.remoteLoginSuccess),
        tap(({ response }) => {
          localStorage.setItem(
            'darkMode',
            JSON.stringify(response.user.dark_mode),
          );
        }),
      ),
    { dispatch: false },
  );

  toggleDarkMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.toggleDarkMode),
      withLatestFrom(
        this.store.select(selectCoreDarkMode),
        this.store.select(selectUser),
      ),
      switchMap(([, darkMode, user]) => {
        //  Always store in localStorage

        localStorage.setItem('darkMode', JSON.stringify(darkMode));

        if (!user) {
          // No user: just update state locally
          return of(userObjectActions.toggleDarkModeSuccess({ darkMode }));
        }

        // User present: persist remotely as well
        return this.userService.setDarkMode(darkMode).pipe(
          map(() => userObjectActions.toggleDarkModeSuccess({ darkMode })),
          catchError((error) =>
            of(userObjectActions.toggleDarkModeFail({ error })),
          ),
        );
      }),
    ),
  );

  toggleMainMenu$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(toggleMainMenuExpanded),
        withLatestFrom(this.store.select(selectMainMenuExpanded)),
        switchMap(([action, expanded]) => {
          return this.userService.setMenuStateOpen(expanded);
        }),
      ),
    { dispatch: false },
  );

  /**
   * Entering an object: the POST that records it, plus an optimistic prepend.
   *
   * The prepend runs *before* the POST, so the list the user sees updates on
   * navigation rather than on the response. A failed POST is not rolled back.
   */
  updateRecentObject$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.objectChanged),
        filter((action) => !!action.object), // Ensure action.object is not null or undefined
        tap((action) => this.recentObjects.prepend(action.object)),
        mergeMap((action) =>
          this.userApi
            .addRecentObject({
              object_id: action.object.id,
              object_class_id: action.object.object_class_id,
              object_type_id: action.object.object_type_id,
            })
            .pipe(
              catchError((error) => {
                // Handle the error or log it
                console.error('Error adding recent object', error);
                return of({}); // Return an observable of an empty object or a more appropriate fallback action
              }),
            ),
        ),
      ),
    { dispatch: false },
  );

  switchUserCompanySuccessRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.switchUserCompanySuccess),
        withLatestFrom(
          this.store.select(selectUserAccessLevel),
          this.store.select(selectCoreAvailableModules),
          this.store.select(selectCoreAppSection),
        ),
        tap(([action, userAccessLevel, availableModules, appSection]) => {
          const currentModule = availableModules.find(
            (module) => module.name.toLowerCase() === appSection,
          );

          if (currentModule && userAccessLevel >= currentModule.accessLevel) {
            this.router.navigate([currentModule.url]);
          } else {
            this.router.navigate(['/analytics']);
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private userService: UserService,
    private objectService: ObjectService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private router: Router,
    private queryClient: QueryClient,
    private userApi: UserApiService,
    private userWorkspaces: UserWorkspacesService,
    private workspaceObjects: WorkspaceObjectsService,
    private recentObjects: RecentObjectsService,
    // Injected for its side effects only. Root providers are constructed lazily and
    // `CoreEffects` is registered at bootstrap, so this injection is what starts the
    // service's `pv-widgets` query and `changeWidgetsLanaguage` derivation.
    private widgetViewPreset: WidgetViewPresetService,
  ) {}
}
